import { HttpClient } from "@angular/common/http";
import { Injectable } from "@angular/core";
import { TranslateService } from "@ngx-translate/core";
import { Observable } from "rxjs";
import { map } from "rxjs/operators";
import { Contract } from "src/app/models/Contract";
import { ContractDocument } from "src/app/models/ContractDocument";
import { ContractLine } from "src/app/models/ContractLine";
import { ContractTool } from "src/app/models/ContractTool";
import { environment } from "src/environments/environment";

import ApiVersionConstants from "../constants/api-version-constants";
import { Column } from "../models/Column";
import { ExcelExportColumn } from "../models/ExcelExport";

@Injectable({
  providedIn: "root",
})
export class ContractService {
  private baseUrl = `${environment.apiConfig.uri}/${ApiVersionConstants.V_2}/contract`;

  constructor(
    private http: HttpClient,
    private translateService: TranslateService,
  ) {}

  getContracts(companyId: string) {
    return this.http.get<Contract[]>(this.baseUrl + "/" + companyId);
  }

  getContractInfo(companyId: string, contractReference: string): Observable<Contract> {
    return this.http
      .get<Contract>(
        `${this.baseUrl}/info?companyId=${companyId}&contractReference=${encodeURIComponent(
          contractReference,
        )}`,
      )
      .pipe(map((response) => new Contract().deserialize(response)));
  }

  getContractLines(companyId: string, contractReference: string) {
    return this.http.get<ContractLine[]>(
      `${this.baseUrl}/lines?companyId=${companyId}&contractReference=${encodeURIComponent(
        contractReference,
      )}`,
    );
  }

  getContractTools(companyId: string, contractReference: string) {
    return this.http.get<ContractTool[]>(
      `${this.baseUrl}/tools?companyId=${companyId}&contractReference=${encodeURIComponent(
        contractReference,
      )}`,
    );
  }

  getContractDocuments(companyId: string, contractReference: string) {
    return this.http.get<ContractDocument[]>(
      `${this.baseUrl}/documents?companyId=${companyId}&contractReference=${encodeURIComponent(
        contractReference,
      )}`,
    );
  }

  getContractDocument(document: ContractDocument): Observable<Blob> {
    return this.http.get(
      `${this.baseUrl}/document?companyId=${
        document.docCompanyId
      }&contractReference=${encodeURIComponent(
        document.contractReference,
      )}&docId=${encodeURIComponent(document.docId)}`,
      { responseType: "blob" },
    );
  }

  public exportToExcel(companyId: string, columns: ExcelExportColumn[]): Observable<Blob> {
    const url = `${this.baseUrl}/downloadExcel/?&companyId=${companyId}`;
    return this.http.post(url, columns, { responseType: "blob" });
  }

  public getContractColumns(): Column[] {
    return [
      new Column("contractReference", "contract.contractreference"),
      new Column("poNumber", "contract.ponumber"),
      new Column("type", "contract.type"),
      new Column("startDate", "contract.startdate"),
      new Column("endDate", "contract.enddate"),
      new Column("isActive", "contract.status"),
    ];
  }

  public getContractToolsColumns(): Column[] {
    return [
      // ToolId and the serial number are exchanged intentionally
      new Column("toolId", "tool.serialnumber"),
      new Column("serialNumber", "tool.toolid"),
      new Column("modelNumber", "tool.modelnumber"),
      new Column("articleNumber", "tool.articlenumber"),
      new Column("description", "tool.description"),
    ];
  }

  public async getAllColumnsForExcelExport(): Promise<ExcelExportColumn[]> {
    const columns = this.getContractColumns();
    const exportColumns: ExcelExportColumn[] = [];

    columns.forEach(async (x) => {
      const translation = await this.translateService.get(x.title).toPromise();
      const col = new ExcelExportColumn(x.key, translation);
      exportColumns.push(col);
    });

    return exportColumns;
  }

  public async getContractToolsColumnsForExcelExport(): Promise<ExcelExportColumn[]> {
    const columns = this.getContractToolsColumns();

    // TODO: V1 has `Status` column. In V2, we don't display status column
    // in the gird. So for now we don't export `Status` in Excel export.
    // Note! if this would be required in future, changes in BE is needed
    // columns.push(
    //   new Column(
    //     "detailedstatus",
    //     LocalizationConstants.CONTRACT_HEADER_COLUMN_DETAILED_STATUS
    //   )
    // );
    const exportColumns: ExcelExportColumn[] = [];

    columns.forEach(async (x) => {
      const translation = await this.translateService.get(x.title).toPromise();
      const col = new ExcelExportColumn(x.key, translation);
      exportColumns.push(col);
    });

    return exportColumns;
  }
}
